import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Play, Pause, Rewind, FastForward, X, Lock, Unlock, Loader2 } from 'lucide-react';
import usePhotoBuffer from '../hooks/usePhotoBuffer';

const PlaybackDrawer = ({
  photos,
  currentIndex,
  setCurrentIndex,
  isPlaying,
  setIsPlaying,
  playbackSpeed,
  setPlaybackSpeed,
  isCameraLocked,
  setIsCameraLocked,
  onClose
}) => {

  const [localValue, setLocalValue] = useState(currentIndex);
  const throttleTimeout = useRef(null);
  const THROTTLE_DELAY_MS = 50;

  // Initialize the Sliding Window Buffer
  // This hook manages fetching signed URLs for the current window [index-5 ... index+30]
  const { cache } = usePhotoBuffer(photos, currentIndex);

  useEffect(() => {
    setLocalValue(currentIndex);
  }, [currentIndex]);

  const throttledSetCurrentIndex = useCallback((value) => {
    if (!throttleTimeout.current) {
      setCurrentIndex(value);
      throttleTimeout.current = setTimeout(() => {
        throttleTimeout.current = null;
      }, THROTTLE_DELAY_MS);
    }
  }, [setCurrentIndex]);

  const handleScrub = (e) => {
    const newValue = Number(e.target.value);
    setLocalValue(newValue);
    throttledSetCurrentIndex(newValue);
  };

  // Get current metadata object
  const currentPhoto = photos[localValue] || photos[currentIndex];
  const firstPhoto = photos[0];
  const lastPhoto = photos[photos.length - 1];

  // Resolve the actual image source from the buffer cache using the filename
  const currentSrc = currentPhoto ? cache[currentPhoto.filename] : null;

  const handleNext = () => {
    setCurrentIndex(prev => Math.min(prev + 1, photos.length - 1));
  };

  const handlePrev = () => {
    setCurrentIndex(prev => Math.max(0, prev - 1));
  };

  const speedOptions = [0.5, 1, 2, 4, 6, 8];

  // Calculate the position of the current file label above the scrubber
  const percentage = photos.length > 1 ? (localValue / (photos.length - 1)) * 100 : 0;

  return (
    <div className="relative w-full h-full bg-white text-gray-900 py-4 px-6 shadow-2xl border-t-2 border-gray-200 overflow-hidden">
      <div className="flex gap-6 h-full">
        {/* Main Image Container */}
        <div className="flex-shrink-0 h-full aspect-video bg-gray-100 flex items-center justify-center rounded-lg border border-gray-200 shadow-sm relative overflow-hidden">
  {currentSrc ? (
    <img 
      src={currentSrc} 
      alt={currentPhoto?.filename}
      className="w-full h-full object-contain overflow-hidden rounded-lg"
    />
  ) : (
    <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
       <Loader2 className="w-8 h-8 animate-spin mb-2" />
       <span className="text-xs font-medium">Buffering...</span>
    </div>
  )}
</div>

        {/* Controls */}
        <div className="flex-grow flex flex-col justify-between py-2">
          <div>
            <h3 className="text-xl font-bold text-gray-900">Station {currentPhoto?.station || 'N/A'}</h3>
            <p className="text-sm text-gray-600 mt-1">{currentPhoto?.filename} ({localValue + 1} / {photos.length})</p>
          </div>

          {/* Scrubber with labels */}
          <div className="relative">
            {/* Current file label above handle */}
            <div 
              className="absolute -top-6 transform -translate-x-1/2 bg-gray-900 text-white px-2 py-1 rounded text-xs whitespace-nowrap z-10"
              style={{ left: `${percentage}%` }}
            >
              {currentPhoto?.filename}
            </div>

            {/* First and last file labels */}
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span className="truncate max-w-[200px]">{firstPhoto?.filename}</span>
              <span className="truncate max-w-[200px]">{lastPhoto?.filename}</span>
            </div>

            <input
              type="range"
              min="0"
              max={photos.length - 1}
              value={localValue}
              onChange={handleScrub}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-red-600"
            />
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-3">
            {/* Playback Controls Group */}
            <div className="flex bg-gray-100 rounded-sm border border-gray-300 overflow-hidden">
              <button 
                onClick={handlePrev} 
                disabled={currentIndex === 0}
                className="p-2 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Previous Frame"
              >
                <Rewind className="text-gray-700 w-4 h-4" />
              </button>
              <button 
                onClick={() => setIsPlaying(!isPlaying)}
                className="p-2 hover:bg-gray-200 transition-colors border-x border-gray-300"
                title={isPlaying ? "Pause" : "Play"}
              >
                {isPlaying ? <Pause className="text-gray-700 w-4 h-4" /> : <Play className="text-gray-700 w-4 h-4" />}
              </button>
              <button 
                onClick={handleNext} 
                disabled={currentIndex === photos.length - 1}
                className="p-2 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                title="Next Frame"
              >
                <FastForward className="text-gray-700 w-4 h-4" />
              </button>
              <button 
                onClick={() => setIsCameraLocked(!isCameraLocked)} 
                className="p-2 hover:bg-gray-200 transition-colors border-l border-gray-300"
                title={isCameraLocked ? "Unlock Camera" : "Lock Camera to Player"}
              >
                {isCameraLocked ? 
                  <Lock className="text-gray-700 w-4 h-4" /> : 
                  <Unlock className="text-gray-700 w-4 h-4" />
                }
              </button>
            </div>
            
            {/* Speed Toggle Group */}
            <div className="flex bg-gray-100 rounded-sm border border-gray-300 overflow-hidden">
              {speedOptions.map((speed) => (
                <button
                  key={speed}
                  onClick={() => setPlaybackSpeed(speed)}
                  className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                    playbackSpeed === speed
                      ? 'bg-red-600 text-white'
                      : 'text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {speed}x
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      <button 
        onClick={onClose} 
        className="absolute top-3 right-3 p-1.5 hover:bg-gray-100 rounded-full transition-colors"
        title="Close Drawer"
      >
        <X size={20} className="text-gray-700" />
      </button>
    </div>
  );
};

export default PlaybackDrawer;